import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plug, ShieldAlert } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { PageHead, Field, Sel, Tgl, inp } from './components';
import AdminBankTransfer from './AdminBankTransfer';
import AdminStripe from './AdminStripe';

export default function AdminPaymentSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/settings/payments');
      setCfg(data.pay_by_bank || {});
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/settings/payments', { pay_by_bank: cfg });
      setCfg(data.pay_by_bank);
      toast.success('Payment settings saved');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/admin/settings/payments/test-connection');
      data.ok ? toast.success(data.message) : toast.warning(data.message);
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  if (!cfg) return <div className="h-40 bg-white animate-pulse border hairline" />;

  const awaiting = cfg.status !== 'active';
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/pay-by-bank`;

  return (
    <div data-testid="admin-payment-settings">
      <PageHead title="Payment Settings" sub="Pay by Bank provider configuration. All secrets are stored server-side only." />

      {awaiting && (
        <div className="bg-[#FFF7ED] border border-[#FDE68A] p-4 flex gap-3 mb-6" data-testid="wallid-status-banner">
          <ShieldAlert className="w-5 h-5 text-[#D97706] shrink-0" />
          <div>
            <p className="font-display font-bold uppercase text-[#92400E]">AWAITING WALLID APPROVAL</p>
            <p className="text-[13px] text-[#92400E] mt-1">Merchant approval and credentials have not been received yet. Pay by Bank is disabled at checkout until valid credentials are configured and the provider is enabled.</p>
          </div>
        </div>
      )}
      {!awaiting && (
        <div className="bg-[#ECFDF5] border border-[#6EE7B7] p-4 mb-6" data-testid="wallid-active-banner">
          <p className="font-display font-bold uppercase text-[#065F46]">Pay by Bank is live</p>
        </div>
      )}

      <div className="border hairline bg-white p-6 space-y-4 max-w-3xl">
        <Sel label="Provider" value={cfg.provider} onChange={(v) => set('provider', v)} testid="pbb-provider"
          options={[['wallid', 'Wallid'], ['custom_open_banking', 'Custom Open Banking Provider'], ['other', 'Other Provider']]} />
        <Sel label="Environment" value={cfg.environment} onChange={(v) => set('environment', v)} testid="pbb-environment"
          options={[['sandbox', 'Sandbox'], ['live', 'Live']]} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Merchant ID"><input data-testid="pbb-merchant-id" value={cfg.merchant_id || ''} onChange={(e) => set('merchant_id', e.target.value)} className={inp} /></Field>
          <Field label="Client ID"><input data-testid="pbb-client-id" value={cfg.client_id || ''} onChange={(e) => set('client_id', e.target.value)} className={inp} /></Field>
          <Field label="Client Secret"><input data-testid="pbb-client-secret" type="password" value={cfg.client_secret || ''} onChange={(e) => set('client_secret', e.target.value)} className={inp} autoComplete="new-password" /></Field>
          <Field label="API Key (if required by official docs)"><input data-testid="pbb-api-key" type="password" value={cfg.api_key || ''} onChange={(e) => set('api_key', e.target.value)} className={inp} autoComplete="new-password" /></Field>
          <Field label="Webhook Secret"><input data-testid="pbb-webhook-secret" type="password" value={cfg.webhook_secret || ''} onChange={(e) => set('webhook_secret', e.target.value)} className={inp} autoComplete="new-password" /></Field>
          <Field label="API Base URL (per official documentation)"><input data-testid="pbb-api-base" value={cfg.api_base_url || ''} onChange={(e) => set('api_base_url', e.target.value)} className={inp} /></Field>
          <Field label="Success Return URL"><input data-testid="pbb-success-url" value={cfg.success_return_url || ''} onChange={(e) => set('success_return_url', e.target.value)} className={inp} /></Field>
          <Field label="Cancel Return URL"><input data-testid="pbb-cancel-url" value={cfg.cancel_return_url || ''} onChange={(e) => set('cancel_return_url', e.target.value)} className={inp} /></Field>
        </div>
        <Tgl label="Enable Pay by Bank at checkout (activates only when credentials are complete)" checked={!!cfg.enabled} onChange={(v) => set('enabled', v)} testid="pbb-enabled" />

        <div className="border hairline bg-[#F4F4F0] p-4 font-mono2 text-xs space-y-1.5">
          <p><span className="text-[#8A8F95]">WEBHOOK URL:</span> <span data-testid="pbb-webhook-url" className="break-all">{webhookUrl}</span></p>
          <p><span className="text-[#8A8F95]">CONNECTION STATUS:</span> <span data-testid="pbb-status" className="uppercase font-semibold">{awaiting ? 'Awaiting merchant approval' : 'Configured'}</span></p>
          <p><span className="text-[#8A8F95]">LAST WEBHOOK:</span> {cfg.last_webhook || 'None received'}</p>
        </div>

        <div className="flex gap-2 pt-2">
          <button data-testid="pbb-save" onClick={save} disabled={saving}
            className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          <button data-testid="pbb-test-connection" onClick={test} disabled={testing}
            className="border border-[#0B0F12] px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-[#F4F4F0] disabled:opacity-50">
            <Plug className="w-3.5 h-3.5" /> {testing ? 'Testing…' : 'Test connection'}
          </button>
        </div>
        <p className="text-[11px] text-[#8A8F95] leading-relaxed">
          Architecture note: checkout talks to a provider-agnostic PaymentProvider interface (createPayment / getPaymentStatus / handleWebhook / cancelPayment / refundPayment).
          Wallid is one adapter; another Open Banking provider can be added without rewriting checkout. Orders are never marked paid from a browser redirect — only from verified server-side confirmation.
        </p>
      </div>

      <AdminBankTransfer />

      <AdminStripe />
    </div>
  );
}
